// =============================================================================
// SecureStorage — Web equivalent of mobile's Keychain + AsyncStorage
//
// Security model:
//   Sensitive data (API credentials, OAuth tokens) is AES-256-GCM encrypted
//   before writing to localStorage. The encryption key is derived from a
//   standalone Turnkey private key's ECDSA signature:
//
//     signRawPayload(privateKeyId, purpose) → SHA-256(r || s) → AES-256-GCM key
//
//   ECDSA + RFC 6979 produces deterministic signatures, so the same private
//   key always derives the same AES key. The key is never cached — derived
//   fresh for each operation, then garbage collected.
//
//   Non-sensitive metadata (flags, IDs, timestamps) uses plain localStorage.
//
// The Polymarket credential threat model is documented in
// docs/eng-and-prod/shared-providers-platform-storage.md.
// =============================================================================

import { notEmpty, validateAndFormatAddress } from "@orb-labs/types";
import type { PlatformStorage, PolymarketL2Credentials, MeshStoredToken, MeshTokenUpdate } from "@orb-labs/orby-react";
import { parsePersistedAppSettings } from "@orb-labs/orby-react";
import type { TurnkeySDKClientBase } from "@turnkey/react-wallet-kit";
import { signForKeyDerivation } from "@/security/TurnkeyKeyManager";

const KEY_DERIVATION_PREFIX = "bullrun:secure-storage:";

/**
 * Validate and format an address for use as a storage key. Throws if the
 * address is malformed so we never silently write to a `<prefix>undefined` key.
 */
function assertFormattedAddress(address: string, label: string): string {
  const formatted = validateAndFormatAddress(address);
  if (!notEmpty(formatted)) {
    throw new Error(`[SecureStorage] Invalid ${label} address: ${address}`);
  }
  return formatted;
}

// =============================================================================
// ENCRYPTION PRIMITIVES
// =============================================================================

const HEX_PAIR_REGEX = /^[0-9a-fA-F]+$/;

/**
 * Derive an AES-256-GCM encryption key from a Turnkey standalone private key.
 *
 * Calls signForKeyDerivation which uses httpClient.signRawPayload — a silent
 * server-side operation authenticated via the Turnkey session. No wallet popup.
 *
 * Throws if the signer returns missing or malformed r/s components. An invalid
 * signature must never silently fall back to a degenerate key — that would
 * produce a deterministic constant key across every failed signing path and
 * encrypt user credentials under it.
 */
export async function deriveEncryptionKey(
  httpClient: TurnkeySDKClientBase,
  privateKeyId: string,
  purpose: string
): Promise<CryptoKey> {
  const result = await signForKeyDerivation(httpClient, privateKeyId, `${KEY_DERIVATION_PREFIX}${purpose}`);
  if (!notEmpty(result.r) || !notEmpty(result.s)) {
    throw new Error("[SecureStorage] Signer returned incomplete signature (missing r or s)");
  }
  const sigHex = result.r + result.s;
  if (sigHex.length === 0 || sigHex.length % 2 !== 0 || !HEX_PAIR_REGEX.test(sigHex)) {
    throw new Error("[SecureStorage] Signer returned malformed signature hex");
  }
  const pairs = sigHex.match(/.{2}/g);
  if (!notEmpty(pairs)) {
    throw new Error("[SecureStorage] Signer returned malformed signature hex");
  }
  const sigBytes = new Uint8Array(pairs.map((b) => parseInt(b, 16)));
  const keyMaterial = await crypto.subtle.digest("SHA-256", sigBytes);

  return crypto.subtle.importKey("raw", keyMaterial, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

/**
 * Encrypt data and store in localStorage.
 *
 * The encryption key is derived from a stable Turnkey private key, so the same
 * key is always recoverable. Using localStorage (rather than sessionStorage)
 * means encrypted tokens survive page reloads — matching mobile's Keychain
 * persistence model.
 */
export async function encryptedStore(key: CryptoKey, storageKey: string, data: unknown): Promise<boolean> {
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);

    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);

    localStorage.setItem(storageKey, btoa(String.fromCharCode(...combined)));
    return true;
  } catch (err) {
    console.error("[SecureStorage] Failed to encrypt and store:", err);
    return false;
  }
}

/**
 * Load and decrypt data from localStorage.
 *
 * Returns null only if the entry is absent. Throws on any failure to decode,
 * decrypt, or parse the ciphertext — the caller must decide whether a
 * corrupt/tampered entry should prompt re-setup, show an error, or be cleared.
 * This function must never delete user secrets on its own, because a transient
 * key-derivation bug could otherwise silently wipe every encrypted value.
 */
export async function encryptedLoad<T>(key: CryptoKey, storageKey: string): Promise<T | null> {
  const encoded = localStorage.getItem(storageKey);
  if (!notEmpty(encoded)) return null;

  let combined: Uint8Array;
  try {
    combined = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
  } catch (err) {
    throw wrapError(`[SecureStorage] Corrupt encrypted entry for ${storageKey}: base64 decode failed`, err);
  }
  if (combined.length <= 12) {
    throw new Error(`[SecureStorage] Corrupt encrypted entry for ${storageKey}: ciphertext too short`);
  }
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
  } catch (err) {
    throw wrapError(`[SecureStorage] Decryption failed for ${storageKey} (tampered ciphertext or wrong key)`, err);
  }
  try {
    return JSON.parse(new TextDecoder().decode(plaintext)) as T;
  } catch (err) {
    throw wrapError(`[SecureStorage] Plaintext JSON parse failed for ${storageKey}`, err);
  }
}

/**
 * Build an Error with a typed `cause`. Using the option-object Error
 * constructor directly requires ES2022 lib; we attach the cause post-hoc for
 * compatibility with the repo's current target.
 */
function wrapError(message: string, cause: unknown): Error {
  const err = new Error(message) as Error & { cause?: unknown };
  err.cause = cause;
  return err;
}

/**
 * Remove an encrypted entry from localStorage.
 */
export function encryptedClear(storageKey: string): void {
  localStorage.removeItem(storageKey);
}

/**
 * Check if an encrypted entry exists without decrypting.
 */
export function encryptedExists(storageKey: string): boolean {
  return notEmpty(localStorage.getItem(storageKey));
}

// =============================================================================
// METADATA STORAGE (plain localStorage, equivalent to mobile AsyncStorage)
// =============================================================================

export function metadataGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function metadataSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.error("[SecureStorage] Failed to set metadata:", err);
  }
}

export function metadataRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Best-effort
  }
}

// =============================================================================
// POLYMARKET L2 CREDENTIALS (encrypted)
// =============================================================================

const POLYMARKET_L2_PREFIX = "polymarket_l2_";

export function polymarketL2Key(address: string): string {
  return `${POLYMARKET_L2_PREFIX}${assertFormattedAddress(address, "Polymarket")}`;
}

export async function storePolymarketL2Credentials(
  key: CryptoKey,
  address: string,
  credentials: PolymarketL2Credentials
): Promise<boolean> {
  return encryptedStore(key, polymarketL2Key(address), credentials);
}

export async function getPolymarketL2Credentials(
  key: CryptoKey,
  address: string
): Promise<PolymarketL2Credentials | undefined> {
  const credentials = await encryptedLoad<PolymarketL2Credentials>(key, polymarketL2Key(address));
  return credentials ?? undefined;
}

export function clearPolymarketL2Credentials(address: string): void {
  encryptedClear(polymarketL2Key(address));
}

export function hasPolymarketL2Credentials(address: string): boolean {
  return encryptedExists(polymarketL2Key(address));
}

// =============================================================================
// MESH TOKEN STORAGE (encrypted)
// =============================================================================

const MESH_TOKEN_PREFIX = "mesh_token_";
const MESH_ACCOUNTS_LIST_KEY = "mesh_linked_accounts";
const MESH_DEVICE_ID_KEY = "mesh_device_id";

// MeshStoredToken is imported from @orb-labs/orby-react — do not redeclare locally.

export async function storeMeshToken(key: CryptoKey, accountAddress: string, token: MeshStoredToken): Promise<boolean> {
  const stored = await encryptedStore(key, `${MESH_TOKEN_PREFIX}${accountAddress}`, token);
  if (stored) {
    addLinkedAccount(accountAddress);
  }
  return stored;
}

export async function getMeshToken(key: CryptoKey, accountAddress: string): Promise<MeshStoredToken | null> {
  return encryptedLoad<MeshStoredToken>(key, `${MESH_TOKEN_PREFIX}${accountAddress}`);
}

export function clearMeshToken(accountAddress: string): void {
  encryptedClear(`${MESH_TOKEN_PREFIX}${accountAddress}`);
  removeLinkedAccount(accountAddress);
}

export async function updateMeshToken(
  encKey: CryptoKey,
  accountAddress: string,
  updates: Partial<Pick<MeshStoredToken, "accessToken" | "refreshToken" | "expiresAt" | "refreshTokenExpiresAt">>
): Promise<boolean> {
  try {
    const existing = await getMeshToken(encKey, accountAddress);
    if (!existing) {
      console.error("[SecureStorage] Cannot update non-existent Mesh token");
      return false;
    }
    return await storeMeshToken(encKey, accountAddress, { ...existing, ...updates });
  } catch (error) {
    console.error("[SecureStorage] Failed to update Mesh token:", error);
    return false;
  }
}

// isMeshAccessTokenExpired / isMeshRefreshTokenExpired are exported from
// @orb-labs/orby-react — do not duplicate here.

// =============================================================================
// LINKED ACCOUNTS METADATA (plain localStorage)
// =============================================================================

export function getLinkedAccountKeys(): string[] {
  try {
    const raw = localStorage.getItem(MESH_ACCOUNTS_LIST_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function addLinkedAccount(accountAddress: string): void {
  const keys = getLinkedAccountKeys();
  if (!keys.includes(accountAddress)) {
    keys.push(accountAddress);
    metadataSet(MESH_ACCOUNTS_LIST_KEY, JSON.stringify(keys));
  }
}

function removeLinkedAccount(accountAddress: string): void {
  const keys = getLinkedAccountKeys().filter((k) => k !== accountAddress);
  metadataSet(MESH_ACCOUNTS_LIST_KEY, JSON.stringify(keys));
}

export function clearAllMeshTokens(): void {
  const keys = getLinkedAccountKeys();
  for (const key of keys) {
    encryptedClear(`${MESH_TOKEN_PREFIX}${key}`);
  }
  metadataRemove(MESH_ACCOUNTS_LIST_KEY);
}

// =============================================================================
// MESH DEVICE ID (plain localStorage — UUID, not sensitive)
// =============================================================================

function generateUUID(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getMeshDeviceId(): string {
  const existing = metadataGet(MESH_DEVICE_ID_KEY);
  if (existing) return existing;

  const newId = generateUUID();
  metadataSet(MESH_DEVICE_ID_KEY, newId);
  return newId;
}

export function clearMeshDeviceId(): void {
  metadataRemove(MESH_DEVICE_ID_KEY);
}

// =============================================================================
// USER SESSION TRACKING (plain localStorage)
// =============================================================================

const LAST_AUTH_USER_KEY = "bullrun_last_auth_user_id";

export function getLastAuthUserId(): string | null {
  return metadataGet(LAST_AUTH_USER_KEY);
}

export function setLastAuthUserId(userId: string): void {
  metadataSet(LAST_AUTH_USER_KEY, userId);
}

export function clearLastAuthUserId(): void {
  metadataRemove(LAST_AUTH_USER_KEY);
}

// =============================================================================
// HYPERLIQUID SETUP STATUS (plain localStorage — boolean flag, not sensitive)
// =============================================================================

const HL_SETUP_KEY_PREFIX = "hl_setup_done_";

function clearAllHyperliquidSetup(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(HL_SETUP_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // Best-effort
  }
}

function hyperliquidSetupKey(address: string): string {
  return `${HL_SETUP_KEY_PREFIX}${assertFormattedAddress(address, "Hyperliquid")}`;
}

function hasHyperliquidSetup(address: string): boolean {
  return notEmpty(metadataGet(hyperliquidSetupKey(address)));
}

function setHyperliquidSetup(address: string): void {
  metadataSet(hyperliquidSetupKey(address), "1");
}

function clearHyperliquidSetup(address: string): void {
  metadataRemove(hyperliquidSetupKey(address));
}

// =============================================================================
// CLEAR ALL (for logout)
// =============================================================================

function clearAllPolymarketL2Credentials(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(POLYMARKET_L2_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // Best-effort
  }
}

export function clearAll(): void {
  clearAllMeshTokens();
  clearMeshDeviceId();
  clearLastAuthUserId();
  clearAllHyperliquidSetup();
  clearAllPolymarketL2Credentials();
}

// =============================================================================
// PLATFORM STORAGE FACTORY
// =============================================================================

/**
 * Mutable ref holding the Turnkey httpClient + privateKeyId pair. The storage
 * object is created once (stable React identity) and reads from this ref at
 * call time. When the Turnkey session establishes and `ensureStorageKey`
 * resolves, the caller sets the ref — encrypted operations start working
 * without re-mounting the provider tree.
 */
export interface EncryptionBackend {
  httpClient: TurnkeySDKClientBase;
  privateKeyId: string;
}

const WEB_SETTINGS_STORAGE_KEY = "bullrun_app_settings";

export function createWebPlatformStorage(backendRef: { current: EncryptionBackend | undefined }): PlatformStorage {
  function requireBackend(): EncryptionBackend {
    const backend = backendRef.current;
    if (!notEmpty(backend)) {
      throw new Error("[SecureStorage] Encryption backend not ready (storage key pending)");
    }
    return backend;
  }

  function isBackendReady(): boolean {
    return notEmpty(backendRef.current);
  }

  async function getPolymarketDerivedKey(): Promise<CryptoKey> {
    const { httpClient, privateKeyId } = requireBackend();
    return deriveEncryptionKey(httpClient, privateKeyId, "polymarket-l2-creds:v1");
  }

  async function getMeshDerivedKey(): Promise<CryptoKey> {
    const { httpClient, privateKeyId } = requireBackend();
    return deriveEncryptionKey(httpClient, privateKeyId, "mesh-tokens");
  }

  return {
    hasHyperliquidSetup: async (address) => hasHyperliquidSetup(address),
    setHyperliquidSetup: async (address) => setHyperliquidSetup(address),
    clearHyperliquidSetup: async (address) => clearHyperliquidSetup(address),

    storePolymarketL2Credentials: async (address, credentials) => {
      if (!isBackendReady()) return false;
      const key = await getPolymarketDerivedKey();
      return storePolymarketL2Credentials(key, address, credentials);
    },
    getPolymarketL2Credentials: async (address) => {
      if (!encryptedExists(polymarketL2Key(address)) || !isBackendReady()) return undefined;
      try {
        const key = await getPolymarketDerivedKey();
        return await getPolymarketL2Credentials(key, address);
      } catch (err) {
        // Never clear the ciphertext here. Reaching this catch can mean:
        //   1. Key derivation failed (transient Turnkey issue) — ciphertext is fine.
        //   2. Decryption failed (tampered ciphertext or wrong key) — caller
        //      should re-prompt setup, and an operator may want the blob preserved
        //      for forensics before it gets overwritten.
        // Silently deleting here would convert a transient signer failure into
        // permanent credential loss.
        console.error("[SecureStorage] Failed to load Polymarket credentials", err);
        return undefined;
      }
    },
    hasPolymarketL2Credentials: async (address) => encryptedExists(polymarketL2Key(address)),
    clearPolymarketL2Credentials: async (address) => clearPolymarketL2Credentials(address),

    // -------------------------------------------------------------------------
    // Mesh token storage (encrypted localStorage with salt "mesh-tokens")
    // -------------------------------------------------------------------------
    storeMeshToken: async (accountAddress: string, token: MeshStoredToken) => {
      if (!isBackendReady()) return false;
      try {
        const key = await getMeshDerivedKey();
        return await storeMeshToken(key, accountAddress, token);
      } catch (err) {
        console.error("[SecureStorage] Failed to store Mesh token:", err);
        return false;
      }
    },
    getMeshToken: async (accountAddress: string) => {
      if (!encryptedExists(`${MESH_TOKEN_PREFIX}${accountAddress}`) || !isBackendReady()) return null;
      try {
        const key = await getMeshDerivedKey();
        return await getMeshToken(key, accountAddress);
      } catch (err) {
        // Do NOT clear — key derivation failed, not decryption. The ciphertext
        // is still valid and will decrypt on a future attempt.
        console.error("[SecureStorage] Failed to load Mesh token:", err);
        return null;
      }
    },
    updateMeshToken: async (accountAddress: string, updates: MeshTokenUpdate) => {
      if (!isBackendReady()) return false;
      try {
        const key = await getMeshDerivedKey();
        return await updateMeshToken(key, accountAddress, updates);
      } catch (err) {
        console.error("[SecureStorage] Failed to update Mesh token:", err);
        return false;
      }
    },
    clearMeshToken: async (accountAddress: string) => clearMeshToken(accountAddress),
    clearAllMeshTokens: async () => clearAllMeshTokens(),
    getLinkedMeshAccountKeys: async () => getLinkedAccountKeys(),
    getMeshDeviceId: async () => getMeshDeviceId(),
    clearMeshDeviceId: async () => clearMeshDeviceId(),

    // Mnemonic — web has no wallet seed import; stubs satisfy the interface
    storeMnemonic: async () => false,
    getMnemonic: async () => null,
    clearMnemonic: async () => {},

    // User session tracking
    getLastAuthUserId: async () => getLastAuthUserId(),
    setLastAuthUserId: async (userId: string) => setLastAuthUserId(userId),
    clearLastAuthUserId: async () => clearLastAuthUserId(),

    // App settings (non-sensitive — plain localStorage). Errors intentionally
    // propagate so `AppSettingsProvider` can catch them via its injected
    // observability and fall through to defaults. Swallowing here hides
    // corrupted payloads and quota-exceeded failures from telemetry.
    loadAppSettings: async () => {
      const raw = localStorage.getItem(WEB_SETTINGS_STORAGE_KEY);
      if (!raw) return undefined;
      // JSON.parse throws on corrupted payloads — let it propagate.
      const parsed = JSON.parse(raw) as unknown;
      // Validate shape against the canonical persisted schema; unknown or
      // legacy fields are dropped rather than fed into the provider.
      return parsePersistedAppSettings(parsed);
    },
    saveAppSettings: (settings) => {
      localStorage.setItem(WEB_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    },
  };
}
